import Heading from "../../../components/heading";
import PageContent from "../../../components/page-content";
import { useEffect } from "preact/hooks";
import { useState } from "preact/hooks";
import PageLoader from "../../../components/page-loader";
import { getEmailTemplates, getEmailTemplate, updateEmailTemplate, createEmailTemplate, sendEmailTemplate } from "../../../api/email-templates";
import EmailTemplateTable from "../../../components/email-template-table";
import { Card, Col, Form, Row, Button } from "react-bootstrap";
import Editor from '@monaco-editor/react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faSpinner, faPaperPlane, faAngleDown, faAngleUp, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { update } from "react-spring";
import { route } from "preact-router";

export default function SaveDocumentTemplate({ id, user }) {

    const [template, setTemplate] = useState({
        key: "",
        name: "",
        subject: "",
        body: "",
        status: true,
        showAdvanced: false,
    });
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templateSaving, setTemplateSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [data, setData] = useState({});
    const [isSending, setIsSending] = useState(false);

    const loadTemplate = async () => {
        try {
            setTemplatesLoading(true);

            const templateResp = await getEmailTemplate(id);

            setTemplate(templateResp.data?.data ?? {});
        } catch (error) {
            setTemplate({});
        } finally {
            setTemplatesLoading(false);
        }

    }

    const handleEditorChange = (value, event) => {
        setTemplate({
            ...template,
            body: value,
        });
    }

    const handleSave = async (e) => {
        e.preventDefault();

        try {
            setTemplateSaving(true);

            if (id) {
                await updateEmailTemplate(id, template);
                loadTemplate();
            } else {
                await createEmailTemplate(template);

                return route("/email-templates");
            }

 
        } catch (error) {
            setTemplate({});
        } finally {
            setTemplateSaving(false);
        }

    }

    const handleChange = (e) => {
        let { id, value } = e.target;

        setTemplate({
            ...template,
            [id]: value,
        });
    }

    const validate = () => {
        let errors = {};

        if (!template.key) {
            errors.key = "La clé est obligatoire";
        }

        if (!template.name) {
            errors.name = "Le nom est obligatoire";
        }

        if (!template.subject) {
            errors.subject = "Le sujet est obligatoire";
        }

        if (!template.body) {
            errors.body = "Le corps est obligatoire";
        }

        setErrors(errors);
    }

    const canSave = () => {
        return Object.keys(errors).length === 0 && !templateSaving;
    }

    const handleSend = async () => {
        try {
            setIsSending(true);

            let to = prompt("À qui envoyer le template e-mail ?", user?.email);

            if (!to) {
                return;
            }

            await sendEmailTemplate(template.key, {
                to: to,
                data: data,
            });

            alert("Le template e-mail a été envoyé avec succès");
        } catch (error) {
            alert("Une erreur est survenue lors de l'envoi du template e-mail");
        } finally {
            setIsSending(false);
        }
    }

    useEffect(() => {
        document.title = "Templates e-mail";

        if (id) {
            loadTemplate();
        }
    }, []);

    useEffect(() => {
        validate();
    }, [template]);

    if (templatesLoading) {
        return <>
            <PageLoader message={"Chargement du template e-mail..."} />
        </>
    }

    return <>
        <PageContent>
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <Button variant="primary" onClick={() => {
                        history.back();
                    }}>
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Retour
                    </Button>

                    <h4 className="mb-0">
                        {id ? <>
                            Modifier <code className="ms-2">{id}</code>
                        </> : "Créer un template e-mail"}
                    </h4>

                    <div className="mt-2">
                        <Button variant="secondary" className="me-2" onClick={() => {
                            handleSend();
                        }}>
                            <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                            Envoyer
                            {isSending ? <>
                                <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />
                            </> : null}
                        </Button>
                        <Button variant="primary" onClick={handleSave} disabled={!canSave()}>
                            {templateSaving ? <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            </> : <>
                                <FontAwesomeIcon icon={faSave} className="me-2" />
                            </>}

                            Sauvegarder
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>

                    <Row>
                        <Col>
                            <Form.Group className="mb-3" controlId="key">
                                <Form.Label>Clé</Form.Label>
                                <Form.Control type="text" placeholder="Clé" value={template.key} onChange={handleChange} isInvalid={errors.key} />
                                {errors.key ? <Form.Control.Feedback type="invalid">{errors.key}</Form.Control.Feedback> : null}
                            </Form.Group>
                        </Col>

                        <Col>
                            <Form.Group className="mb-3" controlId="name">
                                <Form.Label>Titre</Form.Label>
                                <Form.Control type="text" placeholder="Nom" value={template.name} onChange={handleChange} isInvalid={errors.name} />
                                {errors.name ? <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback> : null}
                            </Form.Group>
                        </Col>

                        <Col>
                            <Form.Group className="mb-3" controlId="status">
                                <Form.Label>Statut</Form.Label>
                                <Form.Select value={template.status} onChange={handleChange}>
                                    <option value={true}>Actif</option>
                                    <option value={false}>Inactif</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="subject">
                        <Form.Label>Sujet</Form.Label>
                        <Form.Control type="text" placeholder="Sujet" value={template.subject} onChange={handleChange} isInvalid={errors.subject} />
                        {errors.subject ? <Form.Control.Feedback type="invalid">{errors.subject}</Form.Control.Feedback> : null}
                    </Form.Group>
                </Card.Body>
                <Card.Body>

                    <Editor
                        height="40vh"
                        defaultLanguage="html"
                        defaultValue={template.body}
                        onChange={handleEditorChange}
                        theme="vs-light"
                    />


                </Card.Body>
                <Card.Header onClick={() => {
                    setTemplate({
                        ...template,
                        showAdvanced: !template.showAdvanced,
                    });
                }} className="d-flex justify-content-start align-items-center cursor-pointer gap-4">
                    Données <FontAwesomeIcon icon={template.showAdvanced ? faAngleUp : faAngleDown} />
                </Card.Header>
                <Card.Body className={template.showAdvanced ? "d-block" : "d-none"}>
                    <div className="text-muted mb-4">Les données suivantes seront injectées dans le template e-mail :</div>

                    <textarea placeholder="JSON valide" className="form-control" rows="10" value={JSON.stringify(data, null, 2)} onChange={(e) => setData(JSON.parse(e.target.value))}></textarea>
                </Card.Body>
                <Card.Footer className="d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                        {id ? <>
                            Créé le {new Date(template.created_at).toLocaleString()} par {template.created_by?.email ?? '-'}
                        </> : null}
                    </div>
                    <div className="text-muted">
                        {id ? <>
                            Modifié le {new Date(template.updated_at).toLocaleString()} par {template.updated_by?.email ?? '-'}
                        </> : null}
                    </div>
                </Card.Footer>
            </Card>
        </PageContent>
    </>

}